'use client';


/** Imports */
import classNames from 'classnames';
import {useTranslations} from 'next-intl';
import {ThemeContext} from '@/context/ThemeContext';
import styles from './userChoicePromptQueue.module.css';
import {userChoiceFlags} from '@/modules/userChoiceFlags';
import {addUserChoiceFlagAction} from '@/actions/account';
import {LuMoonStar, LuSparkles, LuSunMedium} from 'react-icons/lu';
import React, {useEffect, useMemo, useRef, useState, useContext} from 'react';


/** Types */
export type UserChoicePrompt = Readonly<{
    id: string;
    type: 'theme-selection';
}>;
type Props = Readonly<{
    initialPrompts: Array<UserChoicePrompt>;
}>;


/** Component */
export default function UserChoicePromptQueue(props: Props) {
    const {initialPrompts} = props;

    // Hooks
    const t = useTranslations('common.userChoicePrompts');
    const {theme, setTheme} = useContext(ThemeContext);

    // Refs
    const dialogRef = useRef<HTMLDialogElement | null>(null);

    // State
    const [prompts, setPrompts] = useState(initialPrompts);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Memos
    const currentPrompt = prompts[0] || null;
    const currentPromptIndex = useMemo(() => {
        if (!currentPrompt) return 0;
        return initialPrompts.findIndex((prompt) => prompt.id === currentPrompt.id) + 1;
    }, [currentPrompt, initialPrompts]);

    // Effects
    useEffect(() => {
        if (!dialogRef.current) return;
        if (currentPrompt && !dialogRef.current.open) dialogRef.current.showModal();
        if (!currentPrompt && dialogRef.current.open) dialogRef.current.close();
    }, [currentPrompt]);
    useEffect(() => {
        if (!currentPrompt) return;
        setError(null);
        setIsSaving(false);
    }, [currentPrompt]);

    // Event handlers
    async function handleThemeSelect(nextTheme: 'light' | 'dark') {
        if (!currentPrompt || isSaving) return;
        setError(null);
        setIsSaving(true);
        try {
            const themeResult = await setTheme(nextTheme, {markChoice: false});
            if (!themeResult.theme?.success) throw new Error(themeResult.theme?.error || 'Failed to save theme');
            const result = await addUserChoiceFlagAction(userChoiceFlags.hasSelectedTheme);
            if (!result?.success) throw new Error(result?.error || 'Failed to save choice');
            setPrompts((prev) => prev.slice(1));
        } catch {
            setError(t('themeSelection.error'));
        } finally {
            setIsSaving(false);
        }
    }


    // Render
    if (!currentPrompt) return null;
    return (
        <dialog
            ref={dialogRef}
            className={styles.dialog}
            onCancel={(event) => event.preventDefault()}
            onClick={(event) => event.preventDefault()}
        >
            <div className={styles.panel}>
                {(prompts.length > 1) && (
                    <div className={styles.queueBadge}>
                        <LuSparkles/>
                        {t('progress', {current: currentPromptIndex, total: initialPrompts.length})}
                    </div>
                )}
                <div className={styles.title}>
                    {t('themeSelection.title')}
                </div>
                <div className={styles.description}>
                    {t('themeSelection.description')}
                </div>
                <div className={styles.options}>
                    <button
                        type="button"
                        className={classNames(
                            styles.option,
                            styles.lightOption,
                            {[styles.active]: (theme === 'light')},
                        )}
                        onClick={() => handleThemeSelect('light')}
                        disabled={isSaving}
                    >
                        <div className={classNames(styles.iconShell, styles.sunIconShell)}>
                            <LuSunMedium className={styles.icon}/>
                        </div>
                        <div className={styles.optionLabel}>
                            {t('themeSelection.light')}
                        </div>
                    </button>
                    <button
                        type="button"
                        className={classNames(
                            styles.option,
                            styles.darkOption,
                            {[styles.active]: (theme === 'dark')},
                        )}
                        onClick={() => handleThemeSelect('dark')}
                        disabled={isSaving}
                    >
                        <div className={classNames(styles.iconShell, styles.moonIconShell)}>
                            <LuMoonStar className={styles.icon}/>
                        </div>
                        <div className={styles.optionLabel}>
                            {t('themeSelection.dark')}
                        </div>
                    </button>
                </div>
                <div className={styles.meta}>
                    {(isSaving && !error) && t('themeSelection.saving')}
                    {(error) && (
                        <span className={styles.error}>
                            {error}
                        </span>
                    )}
                </div>
            </div>
        </dialog>
    );
}
